import type { MailAccount, MailThread } from "../lib/api";

function normalizeFolderId(folder: string | null | undefined): string {
  const value = String(folder || "inbox").trim().toLowerCase();
  if (value === "spam") return "junk";
  if (value === "deleted") return "trash";
  return value || "inbox";
}

/** Stable list/selection key — avoids All Inboxes collisions across accounts. */
export function threadRowKey(thread: Pick<MailThread, "id" | "threadId" | "accountId">): string {
  const raw = thread.threadId ?? thread.id;
  return `${thread.accountId || "acct"}::${String(raw ?? "")}`;
}

/**
 * Id for GET/PATCH /api/mail/:id. Prefer the composite message id (email::jmapId)
 * or scoped Hub ids (gmail_… / atw_…). Bare thread ids are not mailbox-scoped.
 */
export function threadApiId(thread: Pick<MailThread, "id" | "threadId">): string {
  const id = String(thread.id ?? "").trim();
  if (id.includes("@") && id.includes("::")) return id;
  if (id.startsWith("gmail_") || id.startsWith("atw_")) return id;
  const raw = String(thread.threadId ?? thread.id ?? "").trim();
  return raw;
}

export type MailUrlState = {
  account: string;
  folder: string;
  id?: string;
};

export function parseMailSearchParams(params: URLSearchParams): MailUrlState | null {
  const account = params.get("account")?.trim();
  const folder = params.get("folder")?.trim();
  const id = params.get("id")?.trim();
  if (!account && !folder && !id) return null;
  return {
    account: account || "all",
    folder: normalizeFolderId(folder || "inbox"),
    id: id || undefined,
  };
}

/** Query string for /mail deep links (account, folder, scoped id). */
export function buildMailSearchParams(state: MailUrlState): URLSearchParams {
  const params = new URLSearchParams();
  params.set("account", state.account || "all");
  params.set("folder", normalizeFolderId(state.folder || "inbox"));
  if (state.id) params.set("id", state.id);
  return params;
}

export function mailSearchParamsEqual(a: URLSearchParams, b: URLSearchParams): boolean {
  return a.toString() === b.toString();
}

export function accountIdFromScopedMailId(
  apiId: string,
  accounts: MailAccount[],
): string | undefined {
  const id = apiId.trim();
  if (!id) return undefined;
  if (id.startsWith("gmail_") || id.startsWith("atw_")) {
    for (const account of accounts) {
      const prefix = `${id.startsWith("gmail_") ? "gmail" : "atw"}_${account.id}_`;
      if (id.startsWith(prefix)) return account.id;
    }
  }
  if (id.includes("@") && id.includes("::")) {
    const email = id.split("::")[0]?.trim().toLowerCase();
    if (email) {
      const match = accounts.find((row) => row.email?.trim().toLowerCase() === email);
      if (match) return match.id;
    }
  }
  return undefined;
}

export function findThreadForMailUrlId(apiId: string, threads: MailThread[]): MailThread | undefined {
  const trimmed = apiId.trim();
  if (!trimmed) return undefined;
  return threads.find(
    (thread) => threadApiId(thread) === trimmed || threadRowKey(thread) === trimmed,
  );
}

export function stubThreadFromMailUrl(
  apiId: string,
  opts: { accountFilter: string; accounts: MailAccount[] },
): MailThread {
  const fromId = accountIdFromScopedMailId(apiId, opts.accounts);
  const accountId =
    fromId || (opts.accountFilter !== "all" ? opts.accountFilter : undefined);
  return {
    id: apiId,
    threadId: apiId,
    accountId,
    subject: "",
    snippet: "",
    date: new Date().toISOString(),
    messages: [],
  };
}
